import React from "react";
import { fingerLabel } from "./KeyboardDiagram";
import { isNamedKey, keyCapLabel, keyName } from "./arabicText";

function Stat({ value, unit, label, accent }) {
  return (
    <div className="flex-1 text-center px-2">
      <div className="font-cormorant font-semibold leading-none tabular-nums" style={{ fontSize: "2.1rem", color: accent }}>
        {value}
        {unit && <span style={{ fontSize: "1rem", marginLeft: 2 }}>{unit}</span>}
      </div>
      <div className="text-xs mt-1.5" style={{ color: "var(--parchment-dim)" }}>
        {label}
      </div>
    </div>
  );
}

/**
 * The result of one drill, plus the keys that went wrong in it — which is the
 * part worth reading, since it tells the learner what to practise next.
 */
export default function TypingResults({ result, layout, isRecord, onRetry, onNext, nextLabel }) {
  const missed = Object.entries(result.keyErrors || {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  const seconds = Math.max(1, Math.round((result.elapsedMs || 0) / 1000));

  return (
    <div className="flex flex-col gap-5">
      <div className="text-center">
        <h3 className="font-cormorant font-semibold text-2xl" style={{ color: "var(--gold-bright)" }}>
          {result.accuracy >= 98 ? "Clean run" : result.accuracy >= 90 ? "Well done" : "Drill complete"}
        </h3>
        {isRecord && (
          <p className="text-sm mt-1" style={{ color: "var(--teal)" }}>
            A new personal best.
          </p>
        )}
      </div>

      <div
        className="flex items-center rounded-xl py-5"
        style={{ background: "var(--ink)", border: "1px solid var(--panel-line)" }}
      >
        <Stat value={Math.round(result.wpm)} label="words per minute" accent="var(--gold-bright)" />
        <div style={{ width: 1, height: 42, background: "var(--panel-line)" }} />
        <Stat
          value={Math.round(result.accuracy)}
          unit="%"
          label="accuracy"
          accent={result.accuracy >= 95 ? "var(--teal)" : "var(--parchment)"}
        />
        <div style={{ width: 1, height: 42, background: "var(--panel-line)" }} />
        <Stat value={seconds} unit="s" label="time" accent="var(--parchment)" />
      </div>

      {missed.length > 0 && (
        <div>
          <p className="text-sm mb-2" style={{ color: "var(--parchment-dim)" }}>
            Keys to watch:
          </p>
          <div className="flex flex-wrap gap-2">
            {missed.map(([token, count]) => {
              const shift = token.startsWith("Shift+");
              const code = shift ? token.slice(6) : token;
              const spec = layout?.keys?.[code];
              const char = spec ? (shift ? spec.shift : spec.base) : "";
              return (
                <span
                  key={token}
                  className="inline-flex items-center gap-2 rounded-2xl px-3 py-1 text-[0.78rem]"
                  style={{ color: "var(--tag-rose)", border: "1px solid var(--panel-line)" }}
                  title={spec ? fingerLabel(spec.finger) : ""}
                >
                  {shift && <span style={{ color: "var(--parchment-dim)" }}>Shift +</span>}
                  {isNamedKey(char) ? (
                    <span className="text-xs">{keyName(char)}</span>
                  ) : (
                    <span className="font-arabic text-base">{keyCapLabel(char)}</span>
                  )}
                  <span style={{ color: "var(--parchment-dim)" }}>
                    {count} {count === 1 ? "miss" : "misses"}
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onRetry}
          className="dt-ghost-btn flex-1 px-4 py-2.5 min-h-[44px] rounded-xl text-sm font-semibold"
        >
          Try again
        </button>
        <button
          type="button"
          onClick={onNext}
          className="flex-1 px-4 py-2.5 min-h-[44px] rounded-xl text-sm font-semibold transition-[filter] hover:brightness-110"
          style={{ background: "var(--gold)", color: "var(--ink)" }}
        >
          {nextLabel}
        </button>
      </div>
    </div>
  );
}
